import * as React from "react";
import { Grid, AlertColor } from "@mui/material";
import { DataGridPremium, getGridSingleSelectOperators } from "@mui/x-data-grid-premium";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { useDeleteGlCodeMutation, useGetGlDataQuery } from "../../../services/cclTokenedPayrollApi";
import { GlCode } from "../../../services/types/payrollEventTypes";
import AddEditGlCodeDrawer from "./addEditGlCodeDrawer";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

function getDateFromTimestamp(date: any) {
    if (!date) {
        return "";
    }
    return new Date(date).toLocaleDateString();
}

interface GlCodesPanelProps {}

const GlCodesPanel: React.FC<GlCodesPanelProps> = () => {
    const [codeDrawerOpen, setCodeDrawerOpen] = React.useState<boolean>(false);
    const [drawerMode, setDrawerMode] = React.useState<"add" | "edit">("add");
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [selectedCode, setSelectedCode] = React.useState<GlCode | null>(null);

    // Snackbar state
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const { data: glData, isLoading: loading } = useGetGlDataQuery();
    const [deleteGlCode] = useDeleteGlCodeMutation();
    const { logEvent } = useLogAccessEvent();

    const sortedGlCodes = React.useMemo(() => {
        return [...(glData?.glCodes ?? [])].sort((a, b) =>
            String(a.glCode ?? "").localeCompare(String(b.glCode ?? ""))
        );
    }, [glData?.glCodes]);

    const isExactlyOneSelected = selectedRows.length === 1;

    const currentSelectedCode = React.useMemo(() => {
        if (!isExactlyOneSelected) {
            return null;
        }
        const selectedId = String(selectedRows[0] ?? "").trim().toLowerCase();
        return (glData?.glCodes ?? []).find(
            (g) => String(g.glCode ?? "").trim().toLowerCase() === selectedId
        ) ?? null;
    }, [isExactlyOneSelected, selectedRows, glData?.glCodes]);

    const deleteButtonCaption = React.useMemo(() => {
        return currentSelectedCode?.active ? "Disable GL Code" : "Enable GL Code";
    }, [currentSelectedCode?.active]);

    const normalizeSelectionIds = (selection: any): Array<string | number> => {
        if (Array.isArray(selection)) {
            return selection as Array<string | number>;
        }

        if (selection?.ids instanceof Set) {
            return Array.from(selection.ids) as Array<string | number>;
        }

        if (Array.isArray(selection?.ids)) {
            return selection.ids as Array<string | number>;
        }

        return [];
    };

    const handleToolbarButtonClick = (btnId: string) => {
        if (btnId === "add-code-button") {
            setDrawerMode("add");
            setSelectedCode(null);
            setCodeDrawerOpen(true);
        }

        if (btnId === "edit-code-button") {
            if (!isExactlyOneSelected) {
                return;
            }

            const selectedId = String(selectedRows[0] ?? "").trim().toLowerCase();
            const code = (glData?.glCodes ?? []).find(
                (g) => String(g.glCode ?? "").trim().toLowerCase() === selectedId
            );

            setDrawerMode("edit");
            setSelectedCode(
                code ?? {
                    glCode: String(selectedRows[0] ?? ""),
                    description: "",
                    glLetters: "",
                    active: true,
                }
            );
            setCodeDrawerOpen(true);
        }

        if (btnId === "delete-code-button") {
            if (!isExactlyOneSelected || !currentSelectedCode?.glCode) {
                return;
            }

            deleteGlCode(currentSelectedCode.glCode)
                .unwrap()
                .then(() => {
                    logEvent(currentSelectedCode.active ? "GLCodeDisabled" : "GLCodeEnabled", null);
                    setSelectedRows([]);
                    
                    // Show success snackbar
                    const message = currentSelectedCode.active ? "GL Code Disabled" : "GL Code Enabled";
                    setSnackbarMessage(message);
                    setSnackbarSeverity("success");
                    setShowSnackbar(true);
                })
                .catch((error) => {
                    console.error("Error toggling GL code:", error);
                    
                    // Show error snackbar
                    setSnackbarMessage("Failed to toggle GL Code");
                    setSnackbarSeverity("error");
                    setShowSnackbar(true);
                });
        }
    };

    const COLUMNS = [
        {
            field: "glCode",
            headerName: "Code",
            width: 200,
        },
        {
            field: "description",
            headerName: "Description",
            width: 200,
        },
        {
            field: "glLetters",
            headerName: "Letters",
            width: 200,
        },
        {
            field: "active",
            headerName: "Enabled",
            type: "singleSelect",
            valueOptions: [
                { value: true, label: "Yes" },
                { value: false, label: "No" },
            ],
            filterOperators: [...getGridSingleSelectOperators().filter((op) => op.value === "is")],
            width: 200,
            renderCell: (params: any) => {
                return params.value ? "Yes" : "No";
            },
        },
        {
            field: "createdBy",
            headerName: "Created By",
            width: 200,
        },
        {
            field: "created",
            headerName: "Created",
            valueGetter: (params: any) => getDateFromTimestamp(params.row.created),
            width: 200,
        },
        {
            field: "modifiedBy",
            headerName: "Modified By",
            width: 200,
        },
        {
            field: "modified",
            headerName: "Modified",
            valueGetter: (params: any) => getDateFromTimestamp(params.row.modified),
            width: 200,
        }
    ];

    return (
        <React.Fragment>
            <AddEditGlCodeDrawer
                open={codeDrawerOpen}
                mode={drawerMode}
                code={selectedCode}
                onClose={() => setCodeDrawerOpen(false)}
                onSaveResult={(message, severity) => {
                    setSnackbarMessage(message);
                    setSnackbarSeverity(severity);
                    setShowSnackbar(true);
                }}
            />
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={{ justify: "space-between", flexGrow: 1 }}
            >
                <Grid
                    item
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: 1,
                        height: "calc(100vh - 220px)",
                        minHeight: 400,
                    }}
                >
                    <DataGridPremium 
                        columns={COLUMNS}
                        components={{Toolbar: CclDataGridToolbar}}
                        getRowId={(row) => row.glCode}
                        componentsProps={{
                            toolbar: {
                                showSaveState: false,
                                hideSettingsButtons: true,
                                savestate: null,
                                clearstate: null,
                                anySelected: isExactlyOneSelected,
                                miscButtons: [
                                    {
                                        id: "add-code-button",
                                        caption: "Add GL Code",
                                        onClick: null,
                                        returnSelected: false,
                                        restricted: false,
                                    },
                                    {
                                        id: "edit-code-button",
                                        caption: "Edit GL Code",
                                        onClick: null,
                                        returnSelected: false,
                                        restricted: false,
                                        disable: !isExactlyOneSelected,
                                    },
                                    {
                                        id: "delete-code-button",
                                        caption: deleteButtonCaption,
                                        onClick: null,
                                        returnSelected: false,
                                        restricted: false,
                                        disable: !isExactlyOneSelected,
                                    },
                                ],
                                btnClick: handleToolbarButtonClick,
                            },
                        }}
                        checkboxSelection
                        disableSelectionOnClick
                        onSelectionModelChange={(newSelection: any) => {
                            setSelectedRows(normalizeSelectionIds(newSelection));
                        }}
                        rows={sortedGlCodes}
                        loading={loading}

                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default GlCodesPanel;
